import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { ProjectDTO } from '@devrobot/shared';
import { CircleCheck, FolderGit2, GitBranchPlus, Search, SearchX } from 'lucide-react';
import { useMeta, useProjects } from '../api/queries';
import { useUser } from '../api/user';
import { CopyButton } from '../components/CopyButton';
import { EmptyState } from '../components/EmptyState';
import { ErrorAlert } from '../components/ErrorAlert';
import { PageHeader, Section } from '../components/PageHeader';
import { RelativeTime } from '../components/RelativeTime';
import { SkeletonTable } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { pluralize } from '../lib/format';
import { listTransition } from '../kit/hooks';

export function RegisterProjectPanel() {
  const user = useUser();
  const install = useMeta().data?.cliDownload?.installCommand;
  return (
    <Section
      title="Register a project"
      description="Registration starts from the project on your computer: the CLI writes the manifest, creates the GitLab repository in the DevRobot group, and pushes your code."
    >
      {install ? (
        <div>
          <p className="mb-1 text-sm font-medium">Install the CLI (macOS or Linux; Windows through WSL; needs Node.js 22 or later)</p>
          <div className="flex items-center gap-1">
            <div className="mockup-code min-w-0 flex-1 text-sm">
              <pre data-prefix="$">
                <code>{install}</code>
              </pre>
            </div>
            <CopyButton value={install} label="Copy the install command" />
          </div>
          <p className="mt-1 text-xs text-base-content/60">Run the same command again to update. It sets this server as the CLI's default.</p>
        </div>
      ) : null}
      <div className="mockup-code text-sm">
        <pre data-prefix="$">
          <code>devrobot login</code>
        </pre>
        <pre data-prefix="$">
          <code>devrobot init</code>
        </pre>
        <pre data-prefix="$">
          <code>devrobot push</code>
        </pre>
      </div>
      <ol className="list-decimal space-y-1 pl-5 text-sm text-base-content/80">
        <li>
          <code className="font-mono">devrobot init</code> prepares the project: it writes <code className="font-mono">devrobot.yaml</code> and the GitLab CI build configuration.
        </li>
        <li>
          <code className="font-mono">devrobot push</code> registers the project, creates its repository and pushes your branch. Once a branch exists the project becomes deployable.
        </li>
      </ol>
      {!user.canRegisterProjects ? (
        <div role="note" className="alert alert-info alert-soft text-sm">
          Registering needs the GitLab role that may create projects in the DevRobot group. You can still work on projects you are a member of.
        </div>
      ) : null}
    </Section>
  );
}

const TONES = ['primary', 'secondary', 'info', 'accent', 'success'] as const;
function toneFor(key: string) {
  let h = 0;
  for (const c of key) h = (h * 31 + c.charCodeAt(0)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}
const vtName = (prefix: string, key: string) => ({ '--vt': `${prefix}-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}` }) as React.CSSProperties;

type Scope = 'all' | 'deployable' | 'empty';
const SCOPES: [Scope, string][] = [
  ['all', 'All'],
  ['deployable', 'Deployable'],
  ['empty', 'No branches yet'],
];

function ProjectChip({ p, size = 'size-9' }: { p: ProjectDTO; size?: string }) {
  return (
    <span className={`dr-tint-${toneFor(p.key)} grid ${size} shrink-0 place-items-center rounded-field`}>
      <FolderGit2 className="size-4" aria-hidden="true" />
    </span>
  );
}

function DeployableBadge({ p }: { p: ProjectDTO }) {
  return p.deployable ? (
    <span className="badge badge-soft badge-success badge-xs whitespace-nowrap">
      <CircleCheck className="size-3" aria-hidden="true" />
      deployable
    </span>
  ) : (
    <span className="badge badge-soft badge-warning badge-xs whitespace-nowrap" title="The GitLab repository has no branch yet; run devrobot push.">
      <GitBranchPlus className="size-3" aria-hidden="true" />
      no branches yet
    </span>
  );
}

function LastDeployment({ p }: { p: ProjectDTO }) {
  const last = p.lastDeployment;
  if (!last) return <span className="text-base-content/55">Never deployed</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="sr-only">Last deployment</span>
      <StatusBadge kind="job" value={last.state} size="xs" />
      {last.environment ? <span>to {last.environment}</span> : null}
      <RelativeTime value={last.createdAt} className="text-base-content/55" />
    </span>
  );
}

/** Phones: one stacked card per project. */
function ProjectCard({ p }: { p: ProjectDTO }) {
  return (
    <li data-vt style={vtName('prj-card', p.key)}>
      <Link to={`/projects/${p.key}`} className="flex gap-3 px-4 py-3.5 transition-colors duration-150 hover:bg-base-200/50">
        <ProjectChip p={p} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-semibold break-anywhere">{p.name}</h2>
              <p className="font-mono text-xs text-base-content/55">{p.key}</p>
            </div>
            <span className="badge badge-outline badge-sm shrink-0" title="Your GitLab role on this project">
              {p.role}
            </span>
          </div>
          {p.description ? <p className="line-clamp-2 text-sm text-base-content/65">{p.description}</p> : null}
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge kind="registration" value={p.registrationState} size="xs" />
            <DeployableBadge p={p} />
            <span className="badge badge-ghost badge-xs">{pluralize(p.targetCount, 'target')}</span>
          </div>
          <div className="text-xs text-base-content/70">
            <LastDeployment p={p} />
          </div>
        </div>
      </Link>
    </li>
  );
}

export default function ProjectsPage() {
  const q = useProjects();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [scope, setScope] = useState<Scope>('all');

  // Typing settles for a moment before the list re-flows, so rows glide once instead of on every key.
  useEffect(() => {
    if (query === filter) return;
    const t = setTimeout(() => listTransition(() => setFilter(query)), 160);
    return () => clearTimeout(t);
  }, [query, filter]);

  const list = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (q.data ?? []).filter(
      (p) =>
        (scope === 'all' || (scope === 'deployable' ? p.deployable : !p.deployable)) &&
        (!f || p.key.includes(f) || p.name.toLowerCase().includes(f) || p.gitlab.path.toLowerCase().includes(f)),
    );
  }, [q.data, filter, scope]);

  const clear = () =>
    listTransition(() => {
      setQuery('');
      setFilter('');
      setScope('all');
    });

  return (
    <>
      <PageHeader title="Projects" subtitle="Registered applications you can access through your GitLab membership." />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="min-w-0 space-y-4 xl:col-span-2">
          {q.data && q.data.length > 0 ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <label className="input w-full lg:w-72">
                <Search className="size-4 opacity-50" aria-hidden="true" />
                <input type="search" data-page-search className="grow" placeholder="Filter by name, key or path" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Filter projects" />
                <kbd className="kbd kbd-sm max-sm:hidden">/</kbd>
              </label>
              <div role="radiogroup" aria-label="Filter by branch state" className="tabs tabs-box tabs-sm w-fit border border-hairline">
                {SCOPES.map(([k, label]) => (
                  <label key={k} className="tab whitespace-nowrap has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary">
                    <input type="radio" name="projects-scope" className="sr-only" checked={scope === k} onChange={() => listTransition(() => setScope(k))} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          {q.isPending ? <SkeletonTable rows={4} /> : null}
          {q.isError ? <ErrorAlert error={q.error} onRetry={() => void q.refetch()} /> : null}
          {q.data && q.data.length === 0 ? (
            <EmptyState icon={FolderGit2} title="No projects yet">
              You are not a member of any registered project. Register one from your computer with the CLI (see the steps), or ask a maintainer to add you to an existing project in GitLab.
            </EmptyState>
          ) : null}
          {q.data && q.data.length > 0 ? (
            <div className="card dr-surface overflow-hidden">
              {list.length === 0 ? (
                <div className="grid animate-fade place-items-center gap-3 px-6 py-14 text-center">
                  <span className="grid size-12 place-items-center rounded-full bg-base-200 text-base-content/45">
                    <SearchX className="size-6" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-semibold">No project matches {filter ? `“${filter}”` : 'this filter'}</p>
                    <p className="mt-1 text-sm text-base-content/60">Try another name, key or GitLab path.</p>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={clear}>
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  <ul className="divide-y divide-hairline md:hidden">
                    {list.map((p) => (
                      <ProjectCard key={p.id} p={p} />
                    ))}
                  </ul>
                  <div className="overflow-x-auto max-md:hidden">
                    <table className="table">
                      <thead>
                        <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                          <th>Project</th>
                          <th>State</th>
                          <th>Last deployment</th>
                          <th className="text-right">Targets</th>
                          <th className="max-2xl:hidden">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((p) => (
                          <tr key={p.id} data-vt style={vtName('prj-row', p.key)} className="transition-colors duration-150 hover:bg-base-200/50">
                            <td>
                              <div className="flex items-center gap-3">
                                <ProjectChip p={p} />
                                <div className="min-w-0">
                                  <Link to={`/projects/${p.key}`} className="font-semibold break-anywhere hover:underline">
                                    {p.name}
                                  </Link>
                                  <p className="font-mono text-xs text-base-content/55">{p.key}</p>
                                  {p.description ? <p className="line-clamp-1 max-w-72 text-xs text-base-content/55">{p.description}</p> : null}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="flex flex-col items-start gap-1">
                                <StatusBadge kind="registration" value={p.registrationState} size="xs" />
                                <DeployableBadge p={p} />
                              </div>
                            </td>
                            <td className="text-xs text-base-content/70">
                              <LastDeployment p={p} />
                            </td>
                            <td className="text-right text-sm tabular-nums">
                              {p.targetCount}
                              <span className="sr-only"> {p.targetCount === 1 ? 'target' : 'targets'}</span>
                            </td>
                            <td className="max-2xl:hidden">
                              <span className="badge badge-outline badge-sm" title="Your GitLab role on this project">
                                {p.role}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-4 py-3 text-sm text-base-content/60 sm:px-5">
                {list.length === q.data.length ? pluralize(q.data.length, 'project') : `${list.length} of ${pluralize(q.data.length, 'project')}`}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <RegisterProjectPanel />
        </div>
      </div>
    </>
  );
}
